import React from 'react';
import { seriesColor } from '../../styles/tokens';

interface SparklineProps {
  values: number[];
  /** Drives the colour, so the line agrees with the card's PriceChange chip. */
  change: number;
  className?: string;
}

// Drawn in a fixed user-space box and stretched by CSS. Keeps the path maths
// trivial while the card stays responsive; non-scaling-stroke stops the line
// thickening when the box is wider than it is tall.
const VIEW_W = 100;
const VIEW_H = 28;
const PAD = 2; // room for the stroke at the series' min and max

// Smallest price swing, as a fraction of the price, that is allowed to fill
// the box vertically. Without this the chart autoscales to its own min/max,
// so half a basis point of FX noise renders as a cliff next to a "0.00%"
// chip. Moves below this floor stay visibly shallow; real ones still fill it.
const MIN_RELATIVE_RANGE = 0.004;

/**
 * Deliberately hand-rolled rather than a recharts instance: this renders once
 * per market card, and a charting runtime per tile costs far more than the
 * shape is worth. No axes, no tooltip — the price and chip carry the values,
 * the sparkline only carries the trend.
 */
const Sparkline: React.FC<SparklineProps> = ({ values, change, className = '' }) => {
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const mid = (max + min) / 2;
  const step = VIEW_W / (values.length - 1);

  // Scale against the series' own range, but never a range tighter than the
  // floor above, so the vertical exaggeration stays proportional to the move.
  // Also covers the dead-flat case, where the raw range is zero.
  const span = Math.max(max - min, Math.abs(mid) * MIN_RELATIVE_RANGE) || 1;
  const lo = mid - span / 2;

  const y = (v: number) =>
    PAD + (1 - (v - lo) / span) * (VIEW_H - PAD * 2);

  const d = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${y(v).toFixed(2)}`)
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      className={className}
      // The price and the change chip already state the value; announcing the
      // path again would only add noise for screen readers.
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={d}
        fill="none"
        stroke={seriesColor(change)}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export default Sparkline;
